import type { ClubSocial } from "../../lib/api/lidoo.functions";
import { useI18n } from "../../lib/i18n";

// The club's own channels.
//
// For a beach club this is not a nice-to-have. Our photos come from Google and
// are often a season or two old; the club posts what the sand looked like
// yesterday, with this year's loungers and this week's weather. That is exactly
// the question somebody has in the last minute before they ask about a bed.
//
// Drawn in ink rather than in each platform's brand colours. Four saturated
// logos next to a request button turn a calm page into a toolbar, and every one
// of them competes with the single action this page exists for.
const ICON: Record<string, React.ReactNode> = {
  instagram: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  facebook: (
    <path d="M14.5 8.5h2V5.6h-2.3c-2.2 0-3.4 1.3-3.4 3.4v1.6H9v2.9h1.8V21h3.1v-7.5h2.2l.4-2.9h-2.6V9.4c0-.6.2-.9.6-.9z" />
  ),
  tiktok: (
    <path d="M15.5 3.2c.4 2 1.8 3.4 3.8 3.6v2.6c-1.4.1-2.7-.3-3.8-1.1v5.4c0 3.2-2.5 5.5-5.4 5.1-2.4-.3-4.2-2.3-4.3-4.7-.1-2.8 2.1-5.1 4.9-5.1v2.7c-1.2 0-2.2 1-2.2 2.2s1 2.2 2.2 2.2 2.2-1 2.2-2.2V3.2z" />
  ),
  youtube: (
    <>
      <rect x="2.5" y="5.5" width="19" height="13" rx="4" />
      <path d="M10.3 9.6v4.8l4.2-2.4z" fill="currentColor" stroke="none" />
    </>
  ),
};

export function ClubSocials({ socials, slug }: { socials: ClubSocial[]; slug: string }) {
  const { t } = useI18n();
  const known = socials.filter((s) => ICON[s.network]);
  if (known.length === 0) return null;

  return (
    <div className="mt-8">
      <p className="li-mono text-[10px] uppercase tracking-widest text-[#5B6472]">
        {t("socials_title")}
      </p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {known.map((s) => (
          <li key={s.network}>
            <a
              // Through our own hop, like the website link, so a click to their
              // Instagram counts as traffic we sent them. nofollow because we
              // are not vouching for what a third party posts there.
              href={`/go/${slug}/${s.network}`}
              target="_blank"
              rel="noopener nofollow"
              // 40px, above the 24px minimum for a touch target, and enough
              // that the icon is not a pinprick on a phone.
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1C2430]/20 text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6]"
              title={`@${s.handle}`}
              aria-label={`${s.network}: @${s.handle}`}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-[18px] w-[18px]"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {ICON[s.network]}
              </svg>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
